/* =========================
   PEGUU — PURCHASE CONFIRMATION
========================= */

(() => {
  "use strict";


  /* =========================
     CLAVES DE ALMACENAMIENTO
  ========================== */

  const CONFIRMATION_STORAGE_KEY =
    "peguu:purchase-confirmation";

  const MATERIAL_STORAGE_KEY =
    "peguu:selected-material";

  const ORDER_STORAGE_KEY =
    "peguu:sticker-order";

  const DESIGN_STORAGE_KEY =
    "peguu:sticker-design";


  const QR_STORAGE_KEYS = [
    "peguu:qr-config",
    "peguu:sticker-qr",
    "peguu:qr-configuration"
  ];


  const PAYMENT_STORAGE_KEYS = [
    "peguu:sticker-payment",
    "peguu:checkout-contact",
    "peguu:shipping-data",
    "peguu:payment-data"
  ];


  /* =========================
     UTILIDADES
  ========================== */

  function safeParse(
    value
  ) {

    if (
      !value
    ) {
      return null;
    }


    try {

      return JSON.parse(
        value
      );

    } catch (
      error
    ) {

      return null;

    }

  }


  function getStoredObject(
    key
  ) {

    return safeParse(
      sessionStorage.getItem(
        key
      )
    );

  }


  function getFirstStoredObject(
    keys
  ) {

    for (
      const key
      of keys
    ) {

      const value =
        getStoredObject(
          key
        );


      if (
        value &&
        typeof value ===
          "object"
      ) {

        return value;

      }

    }


    return {};

  }


  function getNestedValue(
    object,
    paths = []
  ) {

    if (
      !object ||
      !Array.isArray(
        paths
      )
    ) {
      return null;
    }


    for (
      const path
      of paths
    ) {

      const segments =
        path.split(
          "."
        );


      let current =
        object;


      for (
        const segment
        of segments
      ) {

        if (
          current !== null &&
          current !== undefined &&
          Object.prototype.hasOwnProperty.call(
            current,
            segment
          )
        ) {

          current =
            current[
              segment
            ];

        } else {

          current =
            null;


          break;

        }

      }


      if (
        current !== null &&
        current !== undefined &&
        current !== ""
      ) {

        return current;

      }

    }


    return null;

  }


  function setText(
    element,
    value,
    fallback = "—"
  ) {

    if (
      !element
    ) {
      return;
    }


    const normalizedValue =
      value === null ||
      value === undefined
        ? ""
        : String(
            value
          ).trim();


    element.textContent =
      normalizedValue ||
      fallback;

  }


  /* =========================
     CANTIDAD
  ========================== */

  function normalizeQuantity(
    value
  ) {

    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "—";
    }


    if (
      typeof value ===
        "object"
    ) {

      value =
        value.quantity ||
        value.value ||
        value.amount ||
        "";

    }


    const normalized =
      String(
        value
      ).trim();


    if (
      !normalized
    ) {
      return "—";
    }


    if (
      /pza|pieza/i.test(
        normalized
      )
    ) {

      return normalized;

    }


    return `${normalized} pzas`;

  }


  /* =========================
     MONEDA
  ========================== */

  function formatCurrency(
    value
  ) {

    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "—";
    }


    if (
      typeof value ===
        "string"
    ) {

      const normalized =
        value.trim();


      if (
        normalized.startsWith(
          "$"
        )
      ) {

        return normalized;

      }

    }


    const numericValue =
      Number(
        String(
          value
        ).replace(
          /[^0-9.-]/g,
          ""
        )
      );


    if (
      !Number.isFinite(
        numericValue
      )
    ) {

      return String(
        value
      );

    }


    return numericValue.toLocaleString(
      "es-MX",
      {
        style:
          "currency",

        currency:
          "MXN",

        minimumFractionDigits:
          2,

        maximumFractionDigits:
          2
      }
    );

  }


  /* =========================
     FECHA Y HORA
  ========================== */

  function getFormattedDateParts(
    value
  ) {

    if (
      !value
    ) {

      return {
        date:
          "—",

        time:
          "—"
      };

    }


    const date =
      value instanceof Date
        ? value
        : new Date(
            value
          );


    if (
      Number.isNaN(
        date.getTime()
      )
    ) {

      return {
        date:
          String(
            value
          ),

        time:
          "—"
      };

    }


    const formattedDate =
      new Intl.DateTimeFormat(
        "es-MX",
        {
          day:
            "numeric",

          month:
            "long",

          year:
            "numeric"
        }
      ).format(
        date
      );


    const formattedTime =
      new Intl.DateTimeFormat(
        "es-MX",
        {
          hour:
            "2-digit",

          minute:
            "2-digit",

          hour12:
            false
        }
      ).format(
        date
      );


    return {
      date:
        formattedDate,

      time:
        `${formattedTime} hrs`
    };

  }


  /* =========================
     DISEÑO
  ========================== */

  function getDesignArray(
    designData,
    orderData
  ) {

    const possibleArrays = [
      designData
        ?.designs,

      designData
        ?.files,

      designData
        ?.uploadedFiles,

      orderData
        ?.designs,

      orderData
        ?.files
    ];


    for (
      const possibleArray
      of possibleArrays
    ) {

      if (
        Array.isArray(
          possibleArray
        ) &&
        possibleArray.length >
          0
      ) {

        return possibleArray;

      }

    }


    return [];

  }


  function getDesignName(
    designData,
    orderData
  ) {

    const directName =
      getNestedValue(
        designData,
        [
          "primaryDesign.fileName",
          "primaryDesign.name",
          "primaryFile.fileName",
          "primaryFile.name",
          "design1.fileName",
          "design1.name",
          "fileName",
          "filename",
          "name"
        ]
      );


    if (
      directName
    ) {

      return directName;

    }


    const designs =
      getDesignArray(
        designData,
        orderData
      );


    if (
      designs.length >
      0
    ) {

      const firstDesign =
        designs[
          0
        ];


      return (
        firstDesign
          .fileName ||

        firstDesign
          .filename ||

        firstDesign
          .name ||

        "Diseño cargado"
      );

    }


    const uploadLater =
      getNestedValue(
        designData,
        [
          "uploadLater",
          "willUploadLater",
          "upload_later"
        ]
      );


    if (
      uploadLater
    ) {

      return "Lo subiré más tarde";

    }


    return "Sin diseño";

  }


  function getPreviewImage(
    materialData,
    designData,
    orderData
  ) {

    const directPreview =
      getNestedValue(
        designData,
        [
          "primaryDesign.previewUrl",
          "primaryDesign.preview",
          "primaryDesign.dataUrl",
          "primaryFile.previewUrl",
          "primaryFile.preview",
          "primaryFile.dataUrl",
          "design1.previewUrl",
          "design1.preview",
          "previewUrl",
          "preview",
          "dataUrl",
          "imageData"
        ]
      );


    if (
      directPreview
    ) {

      return directPreview;

    }


    const designs =
      getDesignArray(
        designData,
        orderData
      );


    if (
      designs.length >
      0
    ) {

      const firstDesign =
        designs[
          0
        ];


      const designPreview =
        firstDesign
          .previewUrl ||

        firstDesign
          .preview ||

        firstDesign
          .dataUrl ||

        firstDesign
          .imageData ||

        "";


      if (
        designPreview
      ) {

        return designPreview;

      }

    }


    return (
      getNestedValue(
        materialData,
        [
          "imageUrl",
          "image",
          "thumbnail",
          "featuredImage"
        ]
      ) ||
      ""
    );

  }


  /* =========================
     CONSTRUIR INFORMACIÓN
  ========================== */

  function buildFallbackConfirmation() {

    const materialData =
      getStoredObject(
        MATERIAL_STORAGE_KEY
      ) || {};


    const orderData =
      getStoredObject(
        ORDER_STORAGE_KEY
      ) || {};


    const designData =
      getStoredObject(
        DESIGN_STORAGE_KEY
      ) || {};


    const qrData =
      getFirstStoredObject(
        QR_STORAGE_KEYS
      );


    const paymentData =
      getFirstStoredObject(
        PAYMENT_STORAGE_KEYS
      );


    /* =========================
       NOMBRE DEL CLIENTE
    ========================== */

    const customerName =
      getNestedValue(
        paymentData,
        [
          "fullName",
          "full_name",
          "name",
          "customerName",
          "shippingName",
          "shipping.name"
        ]
      ) ||

      getNestedValue(
        orderData,
        [
          "customerName",
          "fullName",
          "name"
        ]
      ) ||

      "Cliente Peguu";


    /* =========================
       CORREO DEL CLIENTE
    ========================== */

    const customerEmail =
      getNestedValue(
        paymentData,
        [
          "email",
          "customerEmail",
          "shipping.email"
        ]
      ) ||

      getNestedValue(
        orderData,
        [
          "email",
          "customerEmail"
        ]
      ) ||

      "—";


    /* =========================
       MATERIAL / ACABADO
    ========================== */

    const material =
      getNestedValue(
        qrData,
        [
          "finishLabel",
          "finish",
          "selectedFinishLabel",
          "selectedFinish",
          "acabado",
          "material"
        ]
      ) ||

      getNestedValue(
        orderData,
        [
          "materialLabel",
          "finishLabel",
          "selectedMaterialName",
          "materialName",
          "material.name"
        ]
      ) ||

      getNestedValue(
        materialData,
        [
          "name",
          "materialName"
        ]
      ) ||

      "—";


    /* =========================
       CANTIDAD
    ========================== */

    const quantity =
      getNestedValue(
        orderData,
        [
          "quantity",
          "selectedQuantity",
          "cantidad",
          "quote.quantity",
          "pricing.quantity",
          "selection.quantity"
        ]
      );


    /* =========================
       PRECIO UNITARIO
    ========================== */

    const unitPrice =
      getNestedValue(
        orderData,
        [
          "unitPrice",
          "pricePerUnit",
          "precioUnitario",
          "quote.unitPrice",
          "pricing.unitPrice",
          "selectedPrice.unitPrice",
          "selection.unitPrice"
        ]
      );


    /* =========================
       POSICIÓN DEL QR
    ========================== */

    const qrPosition =
      getNestedValue(
        qrData,
        [
          "positionLabel",
          "position",
          "selectedPositionLabel",
          "selectedPosition",
          "qrPosition",
          "posicion"
        ]
      ) ||
      "";


    /* =========================
       URL DEL QR
    ========================== */

    const qrUrl =
      getNestedValue(
        qrData,
        [
          "url",
          "targetUrl",
          "destinationUrl",
          "qrUrl"
        ]
      ) ||
      "";


    return {
      orderNumber:
        "—",

      customerName:
        customerName,

      customerEmail:
        customerEmail,

      orderStatus:
        "Pedido confirmado",

      createdAt:
        new Date()
          .toISOString(),

      material:
        material,

      quantity:
        quantity,

      unitPrice:
        unitPrice,

      designName:
        getDesignName(
          designData,
          orderData
        ),

      previewImage:
        getPreviewImage(
          materialData,
          designData,
          orderData
        ),

      qrPosition:
        qrPosition,

      qrUrl:
        qrUrl
    };

  }


  function getConfirmationData() {

    const storedConfirmation =
      getStoredObject(
        CONFIRMATION_STORAGE_KEY
      );


    if (
      storedConfirmation &&
      typeof storedConfirmation ===
        "object"
    ) {

      return storedConfirmation;

    }


    return {
      id: "",
      orderNumber: "",
      status: "",
      subtotal: null,
      shippingCost: null,
      total: null,
      currency: "MXN",
      createdAt: null
    };

  }


  /* =========================
     MOSTRAR PREVISUALIZACIÓN
  ========================== */

  function populatePreview(
    imageElement,
    placeholderElement,
    imageUrl
  ) {

    if (
      !imageElement ||
      !placeholderElement
    ) {
      return;
    }


    const normalizedUrl =
      imageUrl
        ? String(
            imageUrl
          ).trim()
        : "";


    if (
      !normalizedUrl
    ) {

      imageElement.hidden =
        true;


      imageElement.removeAttribute(
        "src"
      );


      placeholderElement.hidden =
        false;


      return;

    }


    imageElement.src =
      normalizedUrl;


    imageElement.hidden =
      false;


    placeholderElement.hidden =
      true;


    imageElement.addEventListener(
      "error",
      () => {

        imageElement.hidden =
          true;


        imageElement.removeAttribute(
          "src"
        );


        placeholderElement.hidden =
          false;

      },
      {
        once:
          true
      }
    );

  }


  /* =========================
     FILAS OPCIONALES
  ========================== */

  function setExtraRow(
    root,
    name,
    value
  ) {

    const row =
      root.querySelector(
        `[data-confirmation-extra-row="${name}"]`
      );


    if (
      !row
    ) {
      return;
    }


    const hasValue =
      value !== null &&
      value !== undefined &&
      String(
        value
      ).trim() !== "";


    row.hidden =
      !hasValue;

  }


  /* =========================
     INICIALIZAR UNA SECCIÓN
  ========================== */

  function initConfirmation(
    root
  ) {

    if (
      root.dataset
        .confirmationInitialized ===
      "true"
    ) {
      return;
    }


    root.dataset
      .confirmationInitialized =
      "true";


    const confirmationData =
      getConfirmationData();


    /* =========================
       NOMBRE DEL CLIENTE
    ========================== */

    const customerNameElements =
      root.querySelectorAll(
        "[data-confirmation-customer-name]"
      );


    customerNameElements.forEach(
      (
        element
      ) => {

        setText(
          element,
          confirmationData
            .customerName,
          "Cliente Peguu"
        );

      }
    );


    /* =========================
       NÚMERO DE ORDEN
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-order-number]"
      ),
      confirmationData
        .orderNumber
    );


    /* =========================
       NOMBRE DEL PEDIDO
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-order-name]"
      ),
      confirmationData
        .customerName,
      "Cliente Peguu"
    );


    /* =========================
       ESTADO
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-order-status]"
      ),
      confirmationData
        .status ||
      confirmationData
        .orderStatus,
      "Pedido confirmado"
    );


    /* =========================
       FECHA Y HORA
    ========================== */

    const creationDate =
      getFormattedDateParts(
        confirmationData
          .createdAt
      );


    setText(
      root.querySelector(
        "[data-confirmation-order-date]"
      ),
      creationDate
        .date
    );


    setText(
      root.querySelector(
        "[data-confirmation-order-time]"
      ),
      creationDate
        .time
    );


    /* =========================
       MATERIAL
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-material]"
      ),
      confirmationData
        .material
    );


    /* =========================
       CANTIDAD
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-quantity]"
      ),
      normalizeQuantity(
        confirmationData
          .quantity
      )
    );


    /* =========================
       PRECIO UNITARIO
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-unit-price]"
      ),
      formatCurrency(
        confirmationData
          .unitPrice
      )
    );


    /* =========================
       DISEÑO
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-design]"
      ),
      confirmationData
        .designName,
      "Sin diseño"
    );


    /* =========================
       CORREO
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-email]"
      ),
      confirmationData
        .customerEmail
    );


    /* =========================
       POSICIÓN DEL QR
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-qr-position]"
      ),
      confirmationData
        .qrPosition
    );


    /* =========================
       URL DEL QR
    ========================== */

    setText(
      root.querySelector(
        "[data-confirmation-qr-url]"
      ),
      confirmationData
        .qrUrl
    );


    /* =========================
       MOSTRAR FILAS QR
    ========================== */

    setExtraRow(
      root,
      "qr-position",
      confirmationData
        .qrPosition
    );


    setExtraRow(
      root,
      "qr-url",
      confirmationData
        .qrUrl
    );


    /* =========================
       PREVISUALIZACIÓN
    ========================== */

    populatePreview(
      root.querySelector(
        "[data-confirmation-preview]"
      ),

      root.querySelector(
        "[data-confirmation-preview-placeholder]"
      ),

      confirmationData
        .previewImage
    );


    /* =========================
       ENLACE DE RESEÑA
    ========================== */

    const reviewLink =
      root.querySelector(
        "[data-confirmation-review]"
      );


    if (
      reviewLink &&
      reviewLink.dataset
        .reviewEnabled !==
      "true"
    ) {

      reviewLink.addEventListener(
        "click",
        (
          event
        ) => {

          event.preventDefault();

        }
      );

    }


    /* =========================
       HACER OTRO PEDIDO
    ========================== */

    const restartButton =
      root.querySelector(
        "[data-confirmation-restart]"
      );


    if (
      restartButton
    ) {

      restartButton.addEventListener(
        "click",
        () => {

          const materialsUrl =
            root.dataset
              .materialsUrl ||
            "/pages/materiales";

          window.location.href =
            materialsUrl;

        }
      );

    }

  }


  /* =========================
     INICIALIZACIÓN GENERAL
  ========================== */

  function init(
    scope = document
  ) {

    const roots =
      scope.matches?.(
        "[data-peguu-purchase-confirmation]"
      )
        ? [
            scope
          ]
        : Array.from(
            scope.querySelectorAll(
              "[data-peguu-purchase-confirmation]"
            )
          );


    roots.forEach(
      initConfirmation
    );

  }


  /* =========================
     DOM READY
  ========================== */

  if (
    document.readyState ===
    "loading"
  ) {

    document.addEventListener(
      "DOMContentLoaded",
      () => {

        init();

      }
    );

  } else {

    init();

  }


  /* =========================
     SHOPIFY THEME EDITOR
  ========================== */

  document.addEventListener(
    "shopify:section:load",
    (
      event
    ) => {

      init(
        event.target
      );

    }
  );

})();
